import { useMemo } from 'react';
import { useTerminalStore } from '../store/useTerminalStore';
import type { ComboStatus } from '../types/gateway';

const CUSUM_MAX = 5.0;

function scoreColor(score: number): string {
  if (score < 2) return 'bg-green-500';
  if (score < 4) return 'bg-amber-500';
  return 'bg-red-500';
}

function scoreTextColor(score: number): string {
  if (score < 2) return 'text-green-400';
  if (score < 4) return 'text-amber-400';
  return 'text-red-400';
}

function CusumBar({ combo }: { combo: ComboStatus }) {
  const pct = Math.min(100, (Math.abs(combo.cusum_score) / CUSUM_MAX) * 100);

  return (
    <div className="flex items-center gap-2">
      <span className="text-[9px] font-mono text-muted-foreground w-16 truncate" title={combo.combo}>
        {combo.combo}
      </span>
      <div className="flex-1 h-2.5 bg-card-elevated rounded-sm overflow-hidden">
        <div
          className={`h-full rounded-sm transition-all duration-500 ${scoreColor(combo.cusum_score)}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-[9px] font-mono tabular-nums w-10 text-right ${scoreTextColor(combo.cusum_score)}`}>
        {combo.cusum_score.toFixed(2)}
      </span>
    </div>
  );
}

export function CusumGauge({ className = '' }: { className?: string }) {
  const combosMap = useTerminalStore((s) => s.combos);
  const combos = useMemo(() => Object.values(combosMap), [combosMap]);

  return (
    <div className={`bg-card rounded border border-border flex flex-col ${className}`}>
      <div className="drag-handle h-5 flex items-center px-2 cursor-grab active:cursor-grabbing border-b border-border-subtle shrink-0">
        <span className="panel-header">CUSUM</span>
        <span className="ml-auto text-[8px] font-mono text-muted">REGIME</span>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {combos.length > 0 ? (
          combos.map((c) => <CusumBar key={c.combo} combo={c} />)
        ) : (
          <div className="flex items-center justify-center h-full">
            <span className="text-[9px] font-mono text-muted animate-pulse">Awaiting combos...</span>
          </div>
        )}
      </div>
    </div>
  );
}
